import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { supabase } from '@/lib/supabase';
import type {
  InventoryCategory,
  PublicCategoryType,
  SaleMode,
} from '@/features/inventory/types';

export const PUBLIC_TYPE_LABELS: Record<PublicCategoryType, string> = {
  whisky: 'Whiskys',
  gin: 'Gins',
  beer: 'Bières',
  wine: 'Vins',
  spirit: 'Spiritueux',
  food: 'Nourriture',
  cocktail: 'Cocktails',
  other: 'Autres',
};

/** Ordre d'affichage des sections sur /degustation. */
export const PUBLIC_TYPE_ORDER: PublicCategoryType[] = [
  'wine',
  'whisky',
  'gin',
  'spirit',
  'beer',
  'cocktail',
  'food',
  'other',
];

export interface PublicInventoryItem {
  id: string;
  name: string;
  sale_mode: SaleMode;
  ml: number | null;
  quantity: number;
  status: string;
  image_url: string | null;
  description: string | null;
  /** Prix de vente à l'once (calculé en SQL). NULL si sale_mode != a_l_oz, public_type=wine ou infos manquantes. */
  sale_price_per_oz: number | null;
  /** Prix de vente unitaire (calculé en SQL). NULL si sale_mode != par_unite, public_type=wine ou infos manquantes. */
  sale_price_per_unit: number | null;
  /** Prix bouteille (vins uniquement, si wine_sold_as_bottle = true). */
  wine_bottle_price: number | null;
  /** Prix au verre (vins uniquement, si wine_sold_as_glass = true). */
  wine_glass_price: number | null;
  /** True si vendu en bouteille (vins). */
  wine_sold_as_bottle: boolean;
  /** True si vendu au verre (vins). */
  wine_sold_as_glass: boolean;
  category_id: string;
  category_label: string;
  public_type: PublicCategoryType | null;
  category_sort_order: number;
}

const PUBLIC_KEY = ['public-tasting-items'] as const;
const CATEGORIES_KEY = ['inventory-categories', 'with-public'] as const;

/**
 * Liste tous les items inventaire visibles publiquement (vue
 * public_inventory_items côté DB — filtre déjà status=Actif et
 * catégorie is_public=true).
 */
export function usePublicInventoryItems() {
  return useQuery({
    queryKey: PUBLIC_KEY,
    queryFn: async (): Promise<PublicInventoryItem[]> => {
      const { data, error } = await supabase
        .from('public_inventory_items')
        .select('*')
        .order('category_sort_order', { ascending: true })
        .order('name', { ascending: true });
      if (error) throw error;
      // Filtre défensif : rejette les lignes orphelines (id manquant, nom
      // vide, catégorie cassée). Évite un crash de render si la vue SQL
      // public_inventory_items retourne temporairement des données
      // incohérentes pendant une migration ou suppression admin.
      const raw = (data ?? []) as Array<Record<string, unknown>>;
      const cleaned = raw.filter(
        (it) =>
          typeof it.id === 'string' &&
          typeof it.name === 'string' &&
          typeof it.category_id === 'string' &&
          typeof it.category_label === 'string'
      );
      return cleaned as unknown as PublicInventoryItem[];
    },
  });
}

/**
 * Liste TOUTES les catégories d'inventaire avec leurs flags publics
 * (pour la page admin /admin/publications).
 */
export function useInventoryCategoriesWithPublic() {
  return useQuery({
    queryKey: CATEGORIES_KEY,
    queryFn: async (): Promise<InventoryCategory[]> => {
      const { data, error } = await supabase
        .from('inventory_categories')
        .select('*')
        .order('sort_order', { ascending: true });
      if (error) throw error;
      return (data ?? []) as InventoryCategory[];
    },
  });
}

export interface UpdateCategoryPublicationInput {
  id: string;
  is_public?: boolean;
  public_label?: string | null;
  public_type?: PublicCategoryType | null;
}

/**
 * Met à jour les flags publics d'une catégorie d'inventaire.
 */
export function useUpdateCategoryPublication() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async (input: UpdateCategoryPublicationInput) => {
      const { id, ...payload } = input;
      const { error } = await supabase
        .from('inventory_categories')
        .update(payload)
        .eq('id', id);
      if (error) throw error;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: CATEGORIES_KEY });
      queryClient.invalidateQueries({ queryKey: PUBLIC_KEY });
      queryClient.invalidateQueries({ queryKey: ['inventory', 'categories'] });
    },
  });
}

/**
 * Formate un prix de vente pour affichage public. Le prix est déjà
 * calculé en SQL (cout × marge, arrondi 25¢ ↑) — on formate juste.
 * Retourne 'Prix à venir' si pas de prix disponible.
 */
export function formatPublicPrice(
  saleMode: SaleMode,
  perOz: number | null,
  perUnit: number | null
): string {
  if (saleMode === 'a_l_oz' && perOz != null && perOz > 0) {
    return `${perOz.toFixed(2).replace('.', ',')} $ / oz`;
  }
  if (saleMode === 'par_unite' && perUnit != null && perUnit > 0) {
    return `${perUnit.toFixed(2).replace('.', ',')} $`;
  }
  return 'Prix à venir';
}

/**
 * Mappe la quantité disponible en label client-friendly.
 */
export function formatStock(quantity: number): {
  label: string;
  tone: 'ok' | 'low' | 'out';
} {
  if (quantity <= 0) return { label: 'Rupture', tone: 'out' };
  if (quantity < 3) return { label: 'Stock limité', tone: 'low' };
  return { label: 'En stock', tone: 'ok' };
}
